import React, {Fragment, Component} from 'react';
import {
    Box,
    Card,
    CardContent,
} from "@mui/material"
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Button from '../components/Button/Button';
import Footer from '../components/Footer/Footer';
import Header from '../components/Header/Header';
import {PageView} from "../model/enums/PageView";
import { AppLogo, AppLogoImg, DescriptionBox } from '../styles/jss/components/intro';
import { ButtonGroup, CenteredBox, DescriptionTextBox, MainTitle } from '../styles/jss/global';
import { ERROR_MESSAGES } from '../constants/errorMessages';

interface MessageItem {
    message: string;
    cta?: {
        label: string;
        value: string;
    };
}

class Error extends Component {

    classes: any;
    componentProps: any;
    componentEvents: any;

    constructor(props: any) {
        super(props);
        this.classes = props.classes;
        this.componentProps = {
            showHeader: false,
            app: {
                name: props.state.app.name,
                config: props.state.app.config,
                logo: props.state.app.logo
            },
            message: props.state.message || 'Something went wrong, please try again',
            messageItems: props.state.messageItems || [],
            actionMessage: props.state.actionMessage || '',
            allowBack: props.state.allowBack,
            totalPages: props.state.app.schemas ? props.state.app.schemas.uiSchema.elements.length : 0,
            showAppInfo: props.state.showAppInfo,
            appData: props.state.appData
        };

        this.componentEvents = {
            handleShowSpecificPageWithDataClick: props.handleShowSpecificPageWithDataClick,
        };
    }

    componentDidMount() {
        document.body.style.backgroundColor = '#fafafa';
    }

    handleShowPageClick = () => {
        this.componentEvents.handleShowSpecificPageWithDataClick(this.componentProps.appData, this.componentProps.totalPages-1, PageView.FormPages);
    }

    handleCtaClick = (url: string) => {
        try {
            const parsedUrl = new URL(url);

            if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') {
                return;
            }

            window.open(parsedUrl.href, '_blank', 'noopener,noreferrer');
        } catch {
            // Do not navigate when the CTA URL is invalid.
        }
    }

    render() {
        const introPageAppLogoMaxWidth = `${this.componentProps.app.config?.introPageAppLogoMaxWidth !== undefined ?
            this.componentProps.app.config.introPageAppLogoMaxWidth : 75}%`
        return (
            <Fragment>
                <Header {...this.componentProps} />

                <CenteredBox sx={{py:0}} component="main" className="main" maxWidth="md">
                    <Box>
                        {this.componentProps.showAppInfo && this.componentProps.app.logo && introPageAppLogoMaxWidth !== '0%' &&
                            <AppLogo maxWidth={introPageAppLogoMaxWidth}>
                                <AppLogoImg src={this.componentProps.app.logo} alt="App logo" />
                            </AppLogo>
                        }
                        <DescriptionBox>
                            {this.componentProps.showAppInfo && this.componentProps.app.name &&
                                <MainTitle variant={'h3'} component={'h1'}>{this.componentProps.app.name}</MainTitle>
                            }
                            <DescriptionTextBox sx={{color: "rgba(0, 0, 0, 0.54)"}}>{this.componentProps.message}</DescriptionTextBox>

                            {this.componentProps.messageItems.length > 0 && (
                                <Box
                                    sx={{
                                        mt: 3,
                                        mb: 3
                                    }}
                                >
                                    {this.componentProps.messageItems.map((item: MessageItem | string, index: number) => {
                                        const messageItem = typeof item === 'string' ? { message: item } : item;
                                        return (
                                            <Card
                                                key={index}
                                                elevation={0}
                                                sx={{
                                                    mb: 2,
                                                    border: 1,
                                                    borderColor: 'grey.300',
                                                    borderRadius: 2,
                                                    '&:hover': {
                                                        borderColor: 'primary.light',
                                                        bgcolor: 'grey.50',
                                                    },
                                                }}
                                            >
                                                <CardContent sx={{ p: 3 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: messageItem.cta ? 2.5 : 0 }}>
                                                        <InfoOutlinedIcon
                                                            sx={{
                                                                color: 'info.main',
                                                                mr: 2,
                                                                mt: 0.25,
                                                                fontSize: 24,
                                                                flexShrink: 0
                                                            }}
                                                        />
                                                        <Box sx={{ flex: 1 }}>
                                                            <DescriptionTextBox
                                                                sx={{
                                                                    color: 'text.primary',
                                                                    fontSize: '0.95rem',
                                                                    lineHeight: 1.6,
                                                                    m: 0
                                                                }}
                                                            >
                                                                {messageItem.message}
                                                            </DescriptionTextBox>
                                                        </Box>
                                                    </Box>
                                                    {messageItem.cta && (
                                                        <Box sx={{
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            pt: 1
                                                        }}>
                                                            <Button
                                                                variant="contained"
                                                                size="medium"
                                                                color="primary"
                                                                disableElevation
                                                                onClick={() => this.handleCtaClick(messageItem.cta!.value)}
                                                                >
                                                                {messageItem.cta.label}
                                                            </Button>
                                                        </Box>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        );
                                    })}
                                </Box>
                            )}
                            {this.componentProps.actionMessage &&
                                <DescriptionTextBox sx={{
                                    color: "rgba(0, 0, 0, 0.54)",
                                    mt: 2,
                                    p: 2,
                                    bgcolor: 'info.lighter',
                                    borderRadius: 1,
                                    borderLeft: 3,
                                    borderColor: 'info.main'
                                }}>
                                    {this.componentProps.actionMessage}
                                </DescriptionTextBox>
                            }
                        </DescriptionBox>

                        <ButtonGroup>
                            {this.componentProps.allowBack &&
                                <Button
                                    variant="outlined"
                                    size="large"
                                    onClick={this.handleShowPageClick}
                                    classes={{root: 'button-group-item'}}
                                >Back</Button>
                            }
                            {this.componentProps.message !== ERROR_MESSAGES.REQUESTED_APP_NOT_AVAILABLE &&
                                this.componentProps.message !== ERROR_MESSAGES.ITEMS_SUBMISSION_LIMIT_REACHED &&
                                <Button
                                    color="secondary"
                                    variant="contained"
                                    size="large"
                                    disableElevation
                                    onClick={ () => { window.location.reload(); }}
                                    classes={{root: 'button-group-item'}}
                                >Home</Button>}
                        </ButtonGroup>

                    </Box>
                </CenteredBox>

                <Footer />
            </Fragment>
        );
    }
}

export default Error;
